import { useEffect, useRef, useState } from "react";

const WORDMARK = "Billionaire Mindset Technologies";

/**
 * Final brand moment after the footer: oversized wordmark drifting slowly
 * through an atmospheric orbital field, cropped by the viewport edges.
 */
export function BrandSignature() {
  const ref = useRef<HTMLElement | null>(null);
  const [depth, setDepth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const t = 1 - Math.min(1, Math.max(0, rect.top / window.innerHeight));
      setDepth(t);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      ref={ref}
      aria-label="Billionaire Mindset Technologies"
      className="relative isolate overflow-hidden bg-background"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 130% at 50% 130%, color-mix(in oklab, var(--primary) 26%, transparent), transparent 68%)",
          opacity: 0.35 + depth * 0.65,
          transition: "opacity 300ms linear",
        }}
      />
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1440 420"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
      >
        <defs>
          <linearGradient id="bmt-sig-arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0" />
            <stop offset="48%" stopColor="var(--accent)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M -120 360 C 380 150, 1060 150, 1560 360" stroke="url(#bmt-sig-arc)" strokeWidth="1" />
        <path
          d="M -120 430 C 380 210, 1060 210, 1560 430"
          stroke="url(#bmt-sig-arc)"
          strokeWidth="1"
          opacity="0.45"
        />
        <circle
          cx="720"
          cy="252"
          r="2.5"
          fill="white"
          style={{ animation: "bmt-twinkle 6s ease-in-out infinite" }}
        />
        <circle cx="720" cy="252" r="18" fill="var(--primary)" opacity="0.14" />
      </svg>

      <div className="relative py-16 sm:py-20 lg:py-24">
        <div className="marquee-mask flex w-full select-none overflow-hidden">
          <div className="marquee-track flex shrink-0 items-center gap-[0.35em] whitespace-nowrap will-change-transform">
            {[0, 1].map((k) => (
              <span key={k} className="flex shrink-0 items-center gap-[0.35em] pr-[0.35em]">
                <span className="font-display text-[clamp(3rem,13vw,11rem)] leading-[0.86] tracking-[-0.045em] text-foreground/[0.09]">
                  {WORDMARK}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block size-[0.12em] rotate-45 bg-primary/50 text-[clamp(3rem,13vw,11rem)]"
                />
                <span className="font-display text-[clamp(3rem,13vw,11rem)] leading-[0.86] tracking-[-0.045em] outline-figure">
                  {WORDMARK}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block size-[0.12em] rotate-45 bg-primary/50 text-[clamp(3rem,13vw,11rem)]"
                />
              </span>
            ))}
          </div>
        </div>

        <div className="mx-auto mt-12 flex max-w-[1400px] items-center gap-4 px-6 md:px-10">
          <span aria-hidden="true" className="h-px flex-1 bg-hairline" />
          <p className="text-[0.6rem] tracked text-muted-foreground/70">
            Designed, built, deployed, operated
          </p>
          <span aria-hidden="true" className="h-px flex-1 bg-hairline" />
        </div>
      </div>
    </section>
  );
}